import { defineStore } from 'pinia';
import { reactive, ref, toRaw } from 'vue';

import cloneDeep from 'lodash/cloneDeep';

import { toCompanyAddress } from '@/modules/clients/helpers/address';
import { clientService } from '@/modules/clients/services/clientService';
import besoinService from '@/modules/companyNeed/services/companyNeed';
import { type IMissionList, MissionStatus } from '@/types/mission';

import type { CompanyAddress } from '@/modules/clients/types';
import type { CompanyNeedByCompany } from '@/modules/companyNeed/types';
import type { IAddress } from '@/types/address';
import type { CompanyReviews, ICompanyDB } from '@/types/company';
import type { Contact } from '@/types/contact';

const TEMPORARY_ID_PREFIX = 'temp-';

const createTemporaryId = () => `${TEMPORARY_ID_PREFIX}${crypto.randomUUID()}`;

export const isTemporaryId = (id?: string) =>
	!!id?.startsWith(TEMPORARY_ID_PREFIX);

export const useClientStore = defineStore('client', () => {
	const company = ref<ICompanyDB>();
	const companyReviews = ref<CompanyReviews | null>(null);
	const companyContacts = ref<Contact[]>([]);
	const companyContactsCompanyId = ref<string>();
	const companyAddresses = ref<CompanyAddress[]>([]);
	const companyAddressesCompanyId = ref<string>();
	const companyMissions = ref<IMissionList[]>([]);
	const loadingByContactIds = ref<string[]>([]);
	const companyNeeds = ref<CompanyNeedByCompany[]>([]);

	// ---- Fetch data ----
	const fetchCompanyMissions = async (companyId: string) => {
		const allMissionStatus = [
			MissionStatus.created,
			MissionStatus.inPreparation,
			MissionStatus.proposed,
			MissionStatus.ready,
			MissionStatus.completed,
			MissionStatus.inProgress,
			MissionStatus.evaluation,
			MissionStatus.terminated,
			MissionStatus.archived,
			MissionStatus.canceled,
		];

		const fetchMissionsParams = {
			clientId: companyId,
			mode: 'list',
			missionStatus: allMissionStatus,
			noPagination: true,
		};

		const fetchedMissions =
			await clientService.fetchMissions(fetchMissionsParams);
		companyMissions.value = fetchedMissions.missions;
	};

	const fetchCompany = async (companyId: string) => {
		const companyData = await clientService.fetchCompany(companyId);
		company.value = companyData;
	};

	const fetchCompanyReviews = async (companyId: string) => {
		const reviews = await clientService.fetchCompanyReviews(companyId);
		companyReviews.value = reviews;
	};

	const fetchCompanyContacts = async (companyId: string) => {
		if (companyContactsCompanyId.value !== companyId) {
			companyContacts.value = [];
			companyContactsCompanyId.value = companyId;
		}

		const contacts = await clientService.fetchCompanyContacts(companyId);
		if (companyContactsCompanyId.value !== companyId) return;
		companyContacts.value = contacts;
	};

	const fetchCompanyAddresses = async (companyId: string) => {
		if (companyAddressesCompanyId.value !== companyId) {
			companyAddresses.value = [];
			companyAddressesCompanyId.value = companyId;
		}

		const addresses = await clientService.fetchCompanyAddresses(companyId);
		if (companyAddressesCompanyId.value !== companyId) return;
		companyAddresses.value = addresses.map(toCompanyAddress);
	};

	const setCompanyAddresses = (addresses: CompanyAddress[]) => {
		companyAddresses.value = addresses;
	};

	const fetchContactBySalesforceId = async (salesforceId: string) => {
		const contact =
			await clientService.fetchContactBySalesforceId(salesforceId);
		return contact;
	};

	const fetchCompanyNeedsByCompanyId = async (companyId: string) => {
		const result = await besoinService.fetchCompanyNeedsByCompanyId(companyId, {
			page: 1,
			limit: 25,
		});
		companyNeeds.value = result.items;
	};

	const startContactLoading = (contactId: string) => {
		loadingByContactIds.value = [...loadingByContactIds.value, contactId];
	};

	const stopContactLoading = (contactId: string) => {
		loadingByContactIds.value = loadingByContactIds.value.filter(
			(id) => id !== contactId
		);
	};

	// ---- Company contacts actions ----
	const addCompanyContact = async (companyId: string, contact: Contact) => {
		const temporaryId = createTemporaryId();
		companyContacts.value = [
			...companyContacts.value,
			{ ...contact, id: temporaryId },
		];
		startContactLoading(temporaryId);

		try {
			const created = await clientService.addCompanyContact(companyId, contact);
			companyContacts.value = companyContacts.value.map((companyContact) =>
				companyContact.id === temporaryId
					? { ...contact, id: created.id }
					: companyContact
			);
			return created;
		} catch (error) {
			companyContacts.value = companyContacts.value.filter(
				(companyContact) => companyContact.id !== temporaryId
			);
			throw error;
		} finally {
			stopContactLoading(temporaryId);
		}
	};

	const updateCompanyContact = async (
		companyId: string,
		contactId: string,
		contact: Contact
	) => {
		const previousContacts = cloneDeep(toRaw(companyContacts.value));
		companyContacts.value = companyContacts.value.map((companyContact) =>
			companyContact.id === contactId
				? { ...contact, id: contactId }
				: companyContact
		);
		startContactLoading(contactId);

		try {
			await clientService.updateCompanyContact(companyId, contactId, contact);
		} catch (error) {
			companyContacts.value = previousContacts;
			throw error;
		} finally {
			stopContactLoading(contactId);
		}
	};

	const deleteCompanyContact = async (contactId: string) => {
		if (!company.value?.id) {
			throw new Error('Cannot delete company contact: missing company id');
		}

		const previousContacts = cloneDeep(toRaw(companyContacts.value));
		companyContacts.value = companyContacts.value.filter(
			(companyContact) => companyContact.id !== contactId
		);

		try {
			await clientService.deleteCompanyContact(company.value.id, contactId);
		} catch (error) {
			companyContacts.value = previousContacts;
			throw error;
		}
	};

	// ---- Company addresses actions ----
	const addCompanyAddress = async (companyId: string, address: IAddress) => {
		const temporaryId = createTemporaryId();
		companyAddresses.value = [
			...companyAddresses.value,
			{ ...address, id: temporaryId },
		];

		try {
			const created = await clientService.addCompanyAddress(companyId, address);
			companyAddresses.value = companyAddresses.value.map((companyAddress) =>
				companyAddress.id === temporaryId
					? { ...address, id: created.addressId }
					: companyAddress
			);
			return created;
		} catch (error) {
			companyAddresses.value = companyAddresses.value.filter(
				(companyAddress) => companyAddress.id !== temporaryId
			);
			throw error;
		}
	};

	const updateCompanyAddress = async (
		companyId: string,
		addressId: string,
		address: IAddress
	) => {
		const previousAddresses = cloneDeep(toRaw(companyAddresses.value));
		companyAddresses.value = companyAddresses.value.map((companyAddress) =>
			companyAddress.id === addressId
				? { ...companyAddress, ...address, id: addressId }
				: companyAddress
		);

		try {
			await clientService.updateCompanyAddress(companyId, addressId, address);
		} catch (error) {
			companyAddresses.value = previousAddresses;
			throw error;
		}
	};

	const deleteCompanyAddress = async (companyId: string, addressId: string) => {
		const previousAddresses = cloneDeep(toRaw(companyAddresses.value));
		companyAddresses.value = companyAddresses.value.filter(
			(companyAddress) => companyAddress.id !== addressId
		);

		try {
			await clientService.deleteCompanyAddress(companyId, addressId);
		} catch (error) {
			companyAddresses.value = previousAddresses;
			throw error;
		}
	};

	return {
		state: reactive({
			company,
			companyReviews,
			companyContacts,
			companyAddresses,
			companyMissions,
			loadingByContactIds,
			companyNeeds,
		}),
		actions: reactive({
			addCompanyContact,
			updateCompanyContact,
			deleteCompanyContact,
			addCompanyAddress,
			updateCompanyAddress,
			deleteCompanyAddress,
			fetchCompanyAddresses,
			setCompanyAddresses,
			fetchCompanyMissions,
			fetchCompany,
			fetchCompanyReviews,
			fetchCompanyContacts,
			fetchContactBySalesforceId,
			fetchCompanyNeedsByCompanyId,
		}),
	};
});
